import { initializeApp, getApps, getApp, type FirebaseApp } from 'firebase/app';
import {
  getMessaging,
  getToken,
  onMessage,
  type Messaging,
  type MessagePayload,
} from 'firebase/messaging';
import { api } from '../api/client';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'AIzaSyAmZKv3fzv0-7o_5Lnb_j6_-dgBjVBSYr4',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'ecregi-notifications.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'ecregi-notifications',
  storageBucket:
    import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'ecregi-notifications.firebasestorage.app',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '100770082981',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '1:100770082981:web:31a2395ed04473e045dd4c',
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || 'G-Y206RJQ4GQ',
};

const VAPID_KEY =
  import.meta.env.VITE_FIREBASE_VAPID_KEY ||
  'BJAeytBKXjYMppe-6H3750XtgYfK7vdRMc7wx2QB6mXpLL9sWSHO8t5tAxy5xX3Ee6suMLDzXKxipinQ7rR6VpM';

let app: FirebaseApp | null = null;
let messaging: Messaging | null = null;

export function getFirebaseApp(): FirebaseApp {
  if (!app) {
    app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
  }
  return app;
}

export function getFirebaseMessaging(): Messaging | null {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return null;
  }
  if (!messaging) {
    try {
      const appInstance = getFirebaseApp();
      messaging = getMessaging(appInstance);
    } catch (err) {
      console.warn('[Firebase] Messaging not supported or failed to initialize:', err);
    }
  }
  return messaging;
}

export async function requestAndRegisterPushToken(): Promise<string | null> {
  try {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return null;
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.log('[Firebase] Notification permission state:', permission);
      return null;
    }

    const msg = getFirebaseMessaging();
    if (!msg) return null;

    const swRegistration = await navigator.serviceWorker.ready;

    const token = await getToken(msg, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swRegistration,
    });

    if (token) {
      await api.post('/push/tokens', {
        fcmToken: token,
        platform: 'WEB',
        appVersion: '1.0.0',
      });
      // console.log('[Firebase] FCM Token registered with backend:', token);
      return token;
    }
  } catch (err) {
    console.error('[Firebase] Failed to register push token:', err);
  }
  return null;
}

export function setupForegroundNotificationListener(): () => void {
  const msg = getFirebaseMessaging();
  if (!msg) return () => { };

  return onMessage(msg, (payload: MessagePayload) => {
    console.log('[Firebase] Foreground push message received:', payload);

    // Check if the user is currently inside the active chatroom for this transaction
    const currentPath = typeof window !== 'undefined' ? window.location.pathname.toLowerCase() : '';
    const txIdFromPayload = payload.data?.transactionId;
    const isCurrentlyInSameChat =
      typeof txIdFromPayload === 'string' &&
      txIdFromPayload.length > 0 &&
      currentPath.includes(txIdFromPayload.toLowerCase());

    // If user is ALREADY actively viewing this chatroom, do NOT pop up notification & clear badge
    if (isCurrentlyInSameChat) {
      clearPwaBadge();
      return;
    }

    const unreadCountStr = payload.data?.unreadCount;
    if (unreadCountStr) {
      const count = parseInt(unreadCountStr, 10);
      if (!isNaN(count) && 'setAppBadge' in navigator) {
        navigator.setAppBadge(count).catch((e) => console.log('setAppBadge error:', e));
      }
    }

    if (Notification.permission === 'granted') {
      const title = payload.notification?.title || 'EcRegi Notification';
      const options: NotificationOptions & { renotify?: boolean } = {
        body: payload.notification?.body || 'New message received',
        icon: '/pwa_icon_192.png',
        badge: '/pwa_icon_192.png',
        tag: (payload.data?.messageId as string) || `msg-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        renotify: true,
        data: payload.data,
      };
      if ('serviceWorker' in navigator) {
        void navigator.serviceWorker.ready.then((reg) => {
          reg.showNotification(title, options);
        });
      } else {
        new Notification(title, options);
      }
    }
  });
}

export function updatePwaBadge(unreadCount: number): void {
  if (typeof window !== 'undefined' && 'setAppBadge' in navigator) {
    if (unreadCount > 0) {
      navigator.setAppBadge(unreadCount).catch((e) => console.log('setAppBadge error:', e));
    } else if ('clearAppBadge' in navigator) {
      navigator.clearAppBadge().catch((e) => console.log('clearAppBadge error:', e));
    }
  }
}

export function clearPwaBadge(): void {
  if (typeof window !== 'undefined' && 'clearAppBadge' in navigator) {
    navigator.clearAppBadge().catch((e) => console.log('clearAppBadge error:', e));
  }
}
